import Box from '@mui/material/Box';
import Button, { ButtonProps } from '@mui/material/Button';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import React, { PropsWithChildren } from 'react';
import { useScreen } from '../../hooks';
import { CloseIcon } from '../../icons';
import { LoadingButtonProps } from '../buttons/LoadingButton';

interface BaseDialogProps extends DialogProps {
  title: string;
  isMobile?: boolean;
  closeIcon?: React.ReactNode;
  footerAction?:
    | React.ReactNode
    | {
        left?: (ButtonProps | LoadingButtonProps) & {
          label?: string;
          to?: string;
        };
        right?: (ButtonProps | LoadingButtonProps) & {
          label?: string;
          to?: string;
        };
      };
}

const BaseDialog: React.FC<PropsWithChildren<BaseDialogProps>> = ({
  title,
  onClose,
  children,
  fullWidth = true,
  isMobile,
  closeIcon,
  footerAction,
  ...props
}) => {
  const { isMobile: screenIsMobile } = useScreen();
  return (
    <Dialog
      onClose={onClose}
      fullWidth={fullWidth}
      fullScreen={isMobile ?? screenIsMobile}
      closeAfterTransition={false}
      {...props}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 4,
          py: 2,
        }}
      >
        <DialogTitle sx={{ wordBreak: 'break-word', maxWidth: '80%' }}>
          {title}
        </DialogTitle>
        {onClose && (
          <Box
            onClick={(e) => onClose(e, 'escapeKeyDown')}
            sx={{ display: 'flex', gap: 2, cursor: 'pointer' }}
          >
            {closeIcon ?? <CloseIcon />}
          </Box>
        )}
      </Box>
      <DialogContent dividers>{children}</DialogContent>
      {footerAction && (
        <DialogActions>
          {typeof footerAction === 'object' &&
          ('left' in footerAction || 'right' in footerAction) ? (
            <Box
              sx={{
                display: 'flex',
                flex: 1,
                alignItems: 'center',
                px: 6,
                py: 1,
                gap: 12,
              }}
            >
              {footerAction.left && <Button fullWidth {...footerAction.left} />}
              {footerAction.right && (
                <Button fullWidth {...footerAction.right} />
              )}
            </Box>
          ) : (
            <Box>{footerAction as React.ReactNode}</Box>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default BaseDialog;
